import type { Permission } from '@/api/permissions';
import {
  type RoleRequestParty,
  type RoleRequestRecord,
  RoleRequestStatus,
  RoleRequestType,
} from '@/api/role-requests/types';

export type PendingPermissionChange = {
  requestId: string;
  granted: boolean;
  initiatorLabel: string;
  initiatedAt: string;
};

export type PendingMemberChange = {
  requestId: string;
  userId: string;
  label: string;
  assign: boolean;
  initiatorLabel: string;
  initiatedAt: string;
};

export const requestPartyLabel = (party: RoleRequestParty): string =>
  party.name || party.email || 'Former user';

const roleIdOf = (request: RoleRequestRecord): string | null =>
  'roleId' in request.payload ? request.payload.roleId : null;

const isPending = (request: RoleRequestRecord) => request.status === RoleRequestStatus.Pending;

export const pendingPermissionChanges = (
  requests: readonly RoleRequestRecord[],
  roleId: string,
): ReadonlyMap<Permission, PendingPermissionChange> =>
  new Map(
    requests.flatMap((request) => {
      if (!isPending(request) || roleIdOf(request) !== roleId) return [];
      if (
        request.type !== RoleRequestType.AddRolePermission &&
        request.type !== RoleRequestType.RemoveRolePermission
      ) {
        return [];
      }

      return [
        [
          request.payload.permission,
          {
            requestId: request.id,
            granted: request.type === RoleRequestType.AddRolePermission,
            initiatorLabel: requestPartyLabel(request.initiator),
            initiatedAt: request.initiatedAt,
          },
        ] as const,
      ];
    }),
  );

export const pendingMemberChanges = (
  requests: readonly RoleRequestRecord[],
  roleId: string,
): PendingMemberChange[] =>
  requests.flatMap((request) => {
    if (!isPending(request) || roleIdOf(request) !== roleId) return [];
    if (
      request.type !== RoleRequestType.AssignRole &&
      request.type !== RoleRequestType.RemoveRole
    ) {
      return [];
    }

    return [
      {
        requestId: request.id,
        userId: request.payload.targetUserId,
        label: request.target ? requestPartyLabel(request.target) : 'Former user',
        assign: request.type === RoleRequestType.AssignRole,
        initiatorLabel: requestPartyLabel(request.initiator),
        initiatedAt: request.initiatedAt,
      },
    ];
  });

export const pendingRequestCountsByRole = (
  requests: readonly RoleRequestRecord[],
): ReadonlyMap<string, number> =>
  requests.reduce((counts, request) => {
    const roleId = roleIdOf(request);
    if (isPending(request) && roleId) counts.set(roleId, (counts.get(roleId) ?? 0) + 1);

    return counts;
  }, new Map<string, number>());

const newestFirst = (left: RoleRequestRecord, right: RoleRequestRecord) =>
  right.initiatedAt.localeCompare(left.initiatedAt);

/**
 * Every request about one role, newest first, plus the request that created it. A `create_role`
 * request names its role but carries no id, so the creation is matched by the name the role had
 * then (the first applied rename's old name, or the current name) and claimed only if it was
 * approved no later than the role's first other request. Several roles can share a name over
 * time, so only the latest such creation is the role's own.
 */
export const roleActivity = (
  requests: readonly RoleRequestRecord[],
  role: { id: string; name: string },
): RoleRequestRecord[] => {
  const own = requests.filter(
    (request) => request.type !== RoleRequestType.CreateRole && roleIdOf(request) === role.id,
  );
  const oldestFirst = [...own].sort((left, right) => newestFirst(right, left));
  const firstRename = oldestFirst.find(
    (request) =>
      request.type === RoleRequestType.RenameRole && request.status === RoleRequestStatus.Approved,
  );
  const createdAs =
    firstRename?.type === RoleRequestType.RenameRole ? firstRename.payload.oldName : role.name;
  const firstActivity = oldestFirst[0]?.initiatedAt;

  const creation = requests
    .filter(
      (request) =>
        request.type === RoleRequestType.CreateRole &&
        request.status === RoleRequestStatus.Approved &&
        request.payload.name === createdAs &&
        (firstActivity === undefined || request.initiatedAt <= firstActivity),
    )
    .sort(newestFirst)[0];

  return [...own, ...(creation ? [creation] : [])].sort(newestFirst);
};

/**
 * People who hold the role or are already waiting to join it. Offering the second group again
 * would only produce a duplicate request that the backend refuses.
 */
export const unavailableMemberIds = (
  memberIds: readonly string[],
  pendingChanges: readonly { userId: string; assign: boolean }[],
): string[] => [
  ...new Set([
    ...memberIds,
    ...pendingChanges.filter((change) => change.assign).map((change) => change.userId),
  ]),
];
